import { type VariationsGroupByFirstLevelButton } from '@cart-services/types'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { CartFilled } from '@pets-ui/icons'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddonProductInfo } from '../AddonProductInfo/AddonProductInfo'
import { AddonProductVariations } from '../AddonProductVariations/AddonProductVariations'
import cartUiI18nInstance from '../i18n/i18nConfig'

export interface DialogSelectAddonProductVariationContentProps {
  currency?: string
  onSubmit?: (selectedVariation: Variation, quantity: number) => void
  quantity?: number
  variationsGroupByFirstLevelButton?: VariationsGroupByFirstLevelButton
  selectedVariation?: Variation
}

export const DialogSelectAddonProductVariationContent = (props: DialogSelectAddonProductVariationContentProps) => {
  const { variationsGroupByFirstLevelButton, currency, onSubmit, quantity, selectedVariation: selectedVariationProp } = props
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })

  const [firstLevelButton, setFirstLevelButton] = useState(selectedVariationProp?.first_level_button || '其他')
  const [currentQuantity, setCurrentQuantity] = useState(quantity || 1)
  const currentItemVariations = variationsGroupByFirstLevelButton?.[firstLevelButton]
  const [selectedVariation, setSelectedVariation] = useState<Variation | undefined>(selectedVariationProp || currentItemVariations?.[0])

  const salePrices = currentItemVariations ? [...new Set(currentItemVariations.map(variation => variation.sale_price))] : []

  const handleSelectProduct = (firstLevelButton: string) => {
    const firstVariation = variationsGroupByFirstLevelButton?.[firstLevelButton]?.[0]
    setSelectedVariation(firstVariation)
    setFirstLevelButton(firstLevelButton)
    setCurrentQuantity(1)
  }

  const handleSelectVariation = (variationId: number) => {
    const variation = currentItemVariations?.find(variation => variation.variation_id === variationId)
    setSelectedVariation(variation)
    setCurrentQuantity(1)
  }

  const handleQuantityChange = (qty = 0) => {
    setCurrentQuantity(qty)
  }

  const handleSubmit = () => {
    if (selectedVariation) {
      onSubmit?.(selectedVariation, currentQuantity)
    }
  }

  const isOutOfStock = typeof selectedVariation?.stock === 'number' && selectedVariation?.stock <= 0
  const maxQuantity = useMemo(() => {
    if (isOutOfStock) return 0
    if (selectedVariation?.limited_quantity) {
      return Math.min(selectedVariation.limited_quantity, selectedVariation.stock)
    }
    return selectedVariation?.stock || 0
  }, [isOutOfStock, selectedVariation?.limited_quantity, selectedVariation?.stock])

  const firstLevelButtons = variationsGroupByFirstLevelButton ? Object.keys(variationsGroupByFirstLevelButton) : []

  return (
    <>
      <VStack py={4} gap={4}>
        <Box px={4}>
          <AddonProductInfo
            thumbnail={selectedVariation?.thumbnail}
            title={selectedVariation?.name}
            currency={currency}
            salePrices={salePrices}
            selectedSalePrice={selectedVariation?.sale_price}
          />
        </Box>
        <Divider />
        <VStack px={4} gap={4}>
          {firstLevelButtons && firstLevelButtons.length > 1
          && (
            <AddonProductVariations
              title={t('cartUi.specification', { defaultValue: '規格' }) || undefined}
              options={firstLevelButtons.map(firstLevelButton => ({
                label: firstLevelButton,
                value: firstLevelButton,
              }))}
              value={selectedVariation?.first_level_button || firstLevelButtons[0]}
              onSelect={handleSelectProduct}
            />
          )}

          {currentItemVariations && currentItemVariations.length > 0
          && (
            <AddonProductVariations
              title={t('cartUi.product', { defaultValue: '商品' }) || undefined}
              options={currentItemVariations.map(variation => ({
                label: variation.name,
                value: variation.variation_id,
                data: variation,
                disabled: variation.is_out_of_stock,
              })) || []}
              value={selectedVariation?.variation_id}
              onSelect={handleSelectVariation}
            />
          )}
        </VStack>
      </VStack>
      <Divider />
      <HStack alignItems="center" px={4} py={2.5} gap={2}>
        <NumberInput
          min={isOutOfStock ? 0 : 1}
          max={maxQuantity}
          value={currentQuantity}
          onChange={handleQuantityChange}
        />
        {maxQuantity === currentQuantity && <Text color={colors.red600}>{t('cartUi.purchaseMaxQuantity', { maxQuantity, defaultValue: `購買上限 ${maxQuantity} 件` })}</Text>}
        <Box flexGrow={1} />
        <Button variant="filled" disabled={isOutOfStock || currentQuantity === 0} startIcon={<CartFilled />} onClick={handleSubmit}>
          {t('cartUi.updateCart', { defaultValue: '更新購物車' })}
        </Button>
      </HStack>
    </>
  )
}
